import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Download, Upload, AlertTriangle } from 'lucide-react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

export default function Backup() {
  const [loading, setLoading] = useState(false);
  const [restoreData, setRestoreData] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const handleBackup = async () => {
    setLoading(true);
    try {
      const res = await adminApi.backup();
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup created successfully');
    } catch (error) {
      toast.error('Backup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (confirmText !== 'RESTORE') {
      toast.error('Please type RESTORE to confirm');
      return;
    }
    if (!restoreData) {
      toast.error('Please select a backup file');
      return;
    }
    setLoading(true);
    try {
      // Detect DR export by presence of `_media` — if present, use replace mode.
      const isDR = !!(restoreData && restoreData._media);
      await adminApi.restore(restoreData, { confirm: 'RESTORE', replace: isDR });
      toast.success(isDR ? 'DR restore complete (media + docs)' : 'Database restored successfully');
      setConfirmText('');
      setRestoreData(null);
    } catch (error) {
      toast.error('Restore failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        setRestoreData(data);
        toast.success('Backup file loaded');
      } catch (error) {
        toast.error('Invalid backup file');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div data-testid="backup-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554] mb-2">Backup & Restore</h1>
              <p className="text-sm text-[#5A6377]">Manage database backups and restore operations</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-6">
                <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-4">Create Backup</h2>
                <p className="text-sm text-[#5A6377] mb-6">
                  Download a complete backup of all collections including employees, salary, KPI, and documents metadata.
                </p>
                <Button
                  data-testid="create-backup-button"
                  onClick={handleBackup}
                  disabled={loading}
                  className="w-full bg-[#2563EB] hover:bg-[#C2151F]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? 'Creating Backup...' : 'Download Backup'}
                </Button>
              </div>

              <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-6">
                <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-4">Restore from Backup</h2>
                <div className="bg-[#FDE7E9] border border-[#E11D2A]/20 rounded-lg p-4 mb-4 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#E11D2A] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#E11D2A]">
                    Warning: Restore will overwrite existing data. An automatic backup will be created before restoring.
                  </p>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="restore-backup-button"
                      variant="outline"
                      className="w-full border-[#E6E9F0]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Restore from File
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Restore Database</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Input
                          type="file"
                          accept=".json"
                          onChange={handleFileUpload}
                          className="border-[#E6E9F0]"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#5A6377] mb-2 block">
                          Type <strong>RESTORE</strong> to confirm:
                        </label>
                        <Input
                          data-testid="restore-confirm-input"
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="RESTORE"
                          className="border-[#E6E9F0]"
                        />
                      </div>
                      <Button
                        data-testid="restore-confirm-button"
                        onClick={handleRestore}
                        disabled={loading || confirmText !== 'RESTORE' || !restoreData}
                        className="w-full bg-[#E11D2A] hover:bg-[#E11D2A]/90"
                      >
                        {loading ? 'Restoring...' : 'Restore Database'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
